import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { atom, useAtomValue, useSetAtom } from "jotai";
import { useCallback, useEffect, useMemo, useState } from "react";

import { ApiError, TimeoutError, api as cyranoApi } from "@/core/api/client";
import { useSession } from "@/core/auth/useSession";
import { isMobileSync } from "@/core/hooks";
import { useCreatorsList } from "@/panels/creators/atoms";
import type { SortKey } from "./vaultQuery";

import {
  type CatalogTag,
  type VaultAutoPreview,
  type VaultBand,
  type VaultItem,
  type VaultLevel,
  type VaultMediaType,
  type VaultProposedCollection,
  type VaultProposedCut,
  type VaultReviewStatus,
  type VaultSaleSignals,
  type VaultStyle,
  type VaultTier,
  type VaultTimelineSegment,
  type VaultTimeOfDay,
  type VaultWatermarkCrop,
  type VaultWatermarkInfo,
} from "./mockData";

/**
 * Vault items partages entre toutes les instances de VaultPanel + VaultItemPanel.
 *
 * Phase 2 wiring : hydrate par `useVaultItems()` depuis Cyrano FastAPI
 * `/api/vault`. Le commit batch d'enrichissement passe par
 * `useCommitVaultEnrichments()` qui PATCH chaque draft puis refetch.
 */
export const vaultItemsAtom = atom<VaultItem[]>([]);

/**
 * Draft d'enrichissement (style + subject + location + action). Persistee
 * dans `pendingEnrichmentsAtom` quand l'user clique "Save" sur un VaultItemPanel.
 *
 * `style` est le `content_category` cote DB (sfw | nsfw | lifestyle). Il
 * change les suggestions subject/location/action et impacte le routing
 * pipeline (NSFW gate, post-correction par categorie). Le seed initial =
 * `item.style` charge depuis l'API, mais l'user peut le re-classer (utile
 * pour un media uploade dans le mauvais tier ou mal auto-classifie).
 *
 * Pourquoi pas un commit direct ? — Real backend l'enrichissement coute des
 * embeddings + LLM judge. Le user batche plusieurs items puis commit tout
 * d'un coup → plusieurs PATCH paralleles + un seul refetch.
 */
export interface EnrichmentDraft {
  /** null = l'user n'a pas encore choisi le content_category (lifestyle/
   *  sfw/nsfw) — UI peut pre-cocher sfw cote selector mais ne forcera pas
   *  la sauvegarde tant que l'user n'a pas valide. */
  style: VaultStyle | null;
  subject: string[];
  location: string[];
  action: string[];
  /** Catalog tags : categorie(s) de catalogue (multi-select). Source unique
   *  pour le routing pipeline depuis le pivot tag-based (2026-05-16). */
  catalog_tags: CatalogTag[];
}

/**
 * Drafts en attente de commit, indexes par item.id.
 * Cleared par `useCommitVaultEnrichments` apres commit.
 */
export const pendingEnrichmentsAtom = atom<Record<string, EnrichmentDraft>>({});


/* ====================================================================== */
/* API types — shape de /api/vault* (cf vault.py backend)                  */
/* ====================================================================== */

export interface ApiVaultItem {
  id: string;
  creator_id: string;
  /** Creator.name cote Neon = slug Convex. Cle de jointure stable
   *  cote frontend (Convex._id Convex != UUID Neon). */
  creator_slug: string;
  title: string;
  /** Nom personnalisé cockpit (renommage, ISOLÉ de l'IA). null/absent si jamais
   *  renommé. Projeté vers VaultItem.custom_name. */
  custom_name?: string | null;
  description?: string | null;
  tier: VaultTier;
  media_type: VaultMediaType | null;
  style: VaultStyle | null;
  duration_s: number | null;
  price_cents: number;
  sends: number;
  /** Commandes PAYÉES où le média a été livré. Optionnel : absent d'un
   *  backend antérieur au 09-10-2026 (le cockpit se déploie avant Cyrano). */
  ventes?: number;
  /** Fans qui ont payé dans les 24 h après l'avoir reçu offert (Francis#2801).
   *  Optionnel pour la même raison que `ventes`. */
  chauffe?: number;
  added_at: string;  // ISO
  is_enriched: boolean;
  preview_available?: boolean;  // default true si absent (backwards-compat)
  /** true si une vignette webp pre-generee existe pour cet item (servie par
   *  `GET /api/vault/items/{id}/thumb`). Optionnel pour rétro-compat : un
   *  backend pre-vignettes ne l'expose pas → projete a false (fallback /preview). */
  thumb?: boolean;
  subject: string[];
  location: string[];
  action: string[];
  catalog_tags?: string[];  // optional pour backwards-compat pre-pivot
  /** `ctt_…` Dropp si CE média a déjà été téléversé sur Dropfans. Optionnel :
   *  exposé par l'API seulement après le déploiement backend (cockpit d'abord),
   *  donc absent → `null` pendant la fenêtre. */
  dropp_content_id?: string | null;
  /** Coordonnees Telegram du message source (groupe Vault + message_id).
   *  Optionnel pour rétro-compat (backend pré-2026-05-25 ne les exposait
   *  pas). Utilisé par le picker du composer slash commands. */
  source_chat_id?: number;
  source_message_id?: number;
  /** Dossier d'organisation cockpit (VaultCollection), ou null/absent. */
  collection_id?: string | null;
  /** true si CE média est coché « Herald » (pool CTA de canal, sélection PAR
   *  MÉDIA — indépendante du dossier). Optionnel pour rétro-compat. */
  herald_enabled?: boolean;
  /** true si Cyrano a le droit d'envoyer CE média (choisi par Kayla, média par
   *  média). Optionnel pour rétro-compat. */
  cyrano_media_enabled?: boolean;
  /** 🌍 La langue d'un vocal parlé (code ISO), `null` sans langue. Optionnel
   *  pour rétro-compat (le cockpit se déploie AVANT Cyrano). */
  langue?: string | null;
  /** Score du ranker média — présent uniquement sur la réponse `/api/vault/ranked`. */
  rank_score?: number;
  /** Étiquettes du ranker (kink+, hors-créneau, déjà envoyé…) — idem. */
  rank_reasons?: string[];

  /* -------------------------------------------------------------------- */
  /* Vault Review — analyse IA en attente de validation (`GET
   * /api/vault/review/queue`, cf `panels/vault-review`). Absents/null pour
   * tout item hors de cette file. Projetés 1:1 (aucune transformation) vers
   * `VaultItem` par `projectToUi` ci-dessous. */
  /* -------------------------------------------------------------------- */
  review_status?: VaultReviewStatus | null;
  level?: VaultLevel | null;
  time_of_day?: VaultTimeOfDay | null;
  /** La vidéo est-elle assez COURTE pour partir en note vidéo (le « rond ») ?
   *  `true` courte → rond · `false` longue → vidéo normale · `null`/absent =
   *  on ne sait pas (pas une vidéo, durée inconnue, ou backend antérieur au
   *  26-08-2026).
   *
   *  ⚠️ DÉRIVÉ par l'API à chaque lecture depuis la durée (jamais stocké, cf
   *  `app/domain/services/vault_ingestion_legere.py`) — donc juste sur TOUT
   *  le Vault, y compris les médias d'avant. Ne remplace pas l'ancien
   *  `sale_signals.video_note_eligible`, il le CORRIGE : celui-là était écrit
   *  par l'analyse et n'était lu par aucun envoi.
   *
   *  `null` n'est pas `false` : sur une durée inconnue l'envoi tente quand
   *  même le rond et laisse ffprobe trancher. */
  video_note_eligible?: boolean | null;
  timeline?: VaultTimelineSegment[];
  sale_signals?: VaultSaleSignals;
  proposed_collection?: VaultProposedCollection | null;
  proposed_cuts?: VaultProposedCut[];
  watermark?: VaultWatermarkInfo;
  analysis_failed?: boolean;
  analysis_warnings?: string[];
  /** Aperçu de vente auto-généré (contrat 07-10, cf `panels/vault-review`
   *  section « Aperçu de vente »). Absent/null tolérés — backend développé
   *  en parallèle, peut ne pas être déployé. */
  auto_preview?: VaultAutoPreview | null;
  /** Rognage watermark auto-généré (mission CROP-E-UI, cf
   *  `panels/vault-review` section « Rognage du filigrane »). Absent/null
   *  tolérés — backend développé en parallèle, peut ne pas être déployé. */
  watermark_crop?: VaultWatermarkCrop | null;

  /** Panneau Enrichissement (mission M-VAULT-doctrine-v1) — cf docblock
   *  `VaultItem.people`/`.suggested_price_eur`/`.sellable_alone` dans
   *  `./mockData` pour le détail du contrat. Optionnels/tolérés absents. */
  people?: number | null;
  suggested_price_eur?: number | null;
  sellable_alone?: boolean | null;
  /** Valeur intrinsèque + bande de valeur, calculées SERVEUR
   *  (`app/shared/pricing_bands.py`). Optionnels : un backend antérieur au
   *  07-08 ne les expose pas → `null`, et le filtre par bande exclut alors
   *  l'item plutôt que de le laisser passer sans savoir le classer. */
  unit_value_eur?: number | null;
  band?: VaultBand | null;
}

interface ApiVaultListResponse {
  items: ApiVaultItem[];
  next_cursor: string | null;
  /** Total RÉEL d'items dans le scope (org ∩ RBAC), indépendant de la
   *  pagination. Renseigné UNIQUEMENT sur la 1re page (cursor absent) ; null
   *  ensuite. Optionnel pour la rétro-compat : un backend pas encore déployé
   *  ne renvoie pas le champ → null → on retombe sur le comptage chargé. */
  total?: number | null;
}


/* ====================================================================== */
/* Projection API → VaultItem                                              */
/* ====================================================================== */

export interface CreatorLookup {
  name: string;
  color: string;
}

export function thumbColorFromId(id: string): string {
  // Hash deterministe → palette stable visuelle pour les vignettes vide.
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  const palette = [
    "#ef4444", "#f59e0b", "#eab308", "#84cc16", "#10b981",
    "#06b6d4", "#3b82f6", "#6366f1", "#8b5cf6", "#ec4899",
  ];
  return palette[h % palette.length]!;
}

/** Export for tests. Pure : `ApiVaultItem` (shape backend) →
 *  `VaultItem` (shape UI consume). Fait la jointure creator (slug→name)
 *  + thumb_color derive + catalog_tags safe cast. */
export function projectToUi(
  api: ApiVaultItem,
  creator: CreatorLookup | undefined,
): VaultItem {
  return {
    id: api.id,
    creator_id: api.creator_id,
    creator_slug: api.creator_slug,
    // Fallback : si la liste Convex n'a pas encore charge OU si le creator
    // a ete supprime cote Convex sans backfill Neon, on affiche le slug brut
    // au lieu d'un tiret peu informatif.
    creator_name: creator?.name ?? api.creator_slug,
    creator_color: creator?.color ?? "#737373",
    title: api.title,
    custom_name: api.custom_name ?? null,
    description: api.description ?? null,
    tier: api.tier,
    media_type: api.media_type,
    style: api.style,
    thumb_color: thumbColorFromId(api.id),
    duration_s: api.duration_s,
    price_cents: api.price_cents,
    sends: api.sends,
    ventes: api.ventes ?? 0,
    chauffe: api.chauffe ?? 0,
    added_at: Date.parse(api.added_at),
    is_enriched: api.is_enriched,
    preview_available: api.preview_available !== false,
    thumb: api.thumb === true,
    subject: api.subject,
    location: api.location,
    action: api.action,
    catalog_tags: Array.isArray(api.catalog_tags) ? (api.catalog_tags as CatalogTag[]) : [],
    dropp_content_id: api.dropp_content_id ?? null,
    source_chat_id: api.source_chat_id ?? null,
    source_message_id: api.source_message_id ?? null,
    collection_id: api.collection_id ?? null,
    herald_enabled: api.herald_enabled === true,
    cyrano_media_enabled: api.cyrano_media_enabled === true,
    langue: api.langue ?? null,
    rank_score: api.rank_score,
    rank_reasons: api.rank_reasons,
    review_status: api.review_status ?? null,
    level: api.level ?? null,
    time_of_day: api.time_of_day ?? null,
    video_note_eligible: api.video_note_eligible ?? null,
    timeline: api.timeline,
    sale_signals: api.sale_signals,
    proposed_collection: api.proposed_collection ?? null,
    proposed_cuts: api.proposed_cuts,
    watermark: api.watermark,
    analysis_failed: api.analysis_failed === true,
    analysis_warnings: api.analysis_warnings,
    auto_preview: api.auto_preview ?? null,
    watermark_crop: api.watermark_crop ?? null,
    people: api.people ?? null,
    suggested_price_eur: api.suggested_price_eur ?? null,
    sellable_alone: api.sellable_alone ?? null,
    unit_value_eur: api.unit_value_eur ?? null,
    band: api.band ?? null,
  };
}


/* ====================================================================== */
/* useVaultItems                                                           */
/* ====================================================================== */

const VAULT_DESKTOP_PAGE_SIZE = 120;
const VAULT_MOBILE_PAGE_SIZE = 60;

export interface VaultItemsFilters {
  q?: string;
  creator_id?: string;
  creator_slug?: string;
  media_type?: VaultMediaType;
  tier?: VaultTier;
  style?: VaultStyle;
  collection_id?: string | null;
  is_enriched?: boolean;
  /** Bande de valeur, filtrée SERVEUR (`A|B|C|D|FREE|INCONNU`). L'API n'en
   *  prend qu'une — au-delà, `applyVaultFilters` fait le tri côté client. */
  band?: VaultBand;
  /** Le POOL Herald : `true` = les médias autorisés à partir en canal PUBLIC,
   *  `false` = ceux qui ne le sont pas. Absent = sans effet. Filtré SERVEUR. */
  herald_enabled?: boolean;
  /** Ce que CYRANO a le droit d'envoyer : `true` = les médias choisis par
   *  Kayla, `false` = les autres. Absent = sans effet. Filtré SERVEUR. */
  cyrano_media_enabled?: boolean;
  /** Avec `q` : cherche AUSSI au sens (23-09) — « 5 min où elle se met un
   *  god ». Le serveur rend une seule page, la plus proche d'abord. */
  sens?: boolean;
  /** Un tri du « Trier par », rangé PAR LE SERVEUR page par page
   *  (Francis#2798, #2801) — jamais en tirant tout le Vault. Absent = par date. */
  tri?: Exclude<SortKey, "recent">;
  /** Avec `tri` : les types rangés en tête, `video`, `photo`, `video,photo`. */
  types?: string;
  /** Retire les vocaux CÔTÉ SERVEUR (Francis#2802) — « Photo ou vidéo ». */
  sans_audio?: boolean;
  /** Une conversation : seulement ce que son fan possède déjà, payé ou
   *  offert — « Ce qu'il a déjà » du picker. Filtré SERVEUR. */
  du_fan?: string;
}

export interface UseVaultItemsResult {
  items: VaultItem[];
  isLoading: boolean;
  error: Error | null;
  /** Charge la page suivante (no-op si plus de page ou deja en cours). */
  fetchNextPage: () => void;
  /** true tant qu'il reste des pages cote serveur (curseur non null). */
  hasNextPage: boolean;
  /** true pendant le fetch d'une page suivante (≠ isLoading = 1ere page). */
  isFetchingNextPage: boolean;
  /** Total RÉEL d'items du scope (org), renvoyé par le backend sur la 1re
   *  page — indépendant de la pagination. null tant qu'inconnu (1re page pas
   *  encore arrivée OU backend sans le champ). Le header du Vault l'affiche
   *  pour montrer « le vrai nombre de médias », pas le nombre chargé. */
  total: number | null;
}

/**
 * Liste paginee des vault items visibles (org-scoped via JWT WorkOS), hydratee
 * depuis `/api/vault` par pages de {@link VAULT_PAGE_SIZE} via curseur
 * keyset (`next_cursor`). Polling 30s — le vault evolue lentement (nouveaux
 * items indexes via Telegram quand le creator post). Sync dans `vaultItemsAtom`.
 *
 * Pagination (2026-06-01) : avant, un seul GET `limit=200` plafonnait le vault
 * a 200 items cote cockpit (le `next_cursor` etait ignore) — au-dela, les items
 * (les plus ANCIENS, ordre DESC) etaient invisibles dans la grille ET les
 * pickers. Desormais `useInfiniteQuery` suit le curseur.
 *
 * @param opts.loadAll — si true, draine automatiquement TOUTES les pages des
 *   que dispo. A utiliser par les consumers qui ont besoin du dataset complet
 *   en memoire : pickers (recherche cross-vault) et resolution d'items par id.
 *   La grille principale (`VaultPanel`) laisse `loadAll=false` et pilote
 *   `fetchNextPage` au scroll (rendu incremental, evite de marteler le proxy
 *   d'apercus). Cache partage par queryKey → si un picker draine tout, les
 *   autres consumers voient aussi tout (idempotent).
 */
export function useVaultItems(opts?: {
  loadAll?: boolean;
  pageSize?: number;
  filters?: VaultItemsFilters;
  enabled?: boolean;
}): UseVaultItemsResult {
  const loadAll = opts?.loadAll ?? false;
  const pageSize =
    opts?.pageSize ?? (isMobileSync() ? VAULT_MOBILE_PAGE_SIZE : VAULT_DESKTOP_PAGE_SIZE);
  const filters = opts?.filters ?? {};
  const enabled = opts?.enabled ?? true;
  const { user } = useSession();
  const isAuthenticated = user !== null;
  const creators = useCreatorsList();

  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ["vault", "list", { pageSize, filters }],
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }): Promise<ApiVaultListResponse> => {
      return cyranoApi.get<ApiVaultListResponse>("/api/vault", {
        params: {
          limit: pageSize,
          ...filters,
          ...(filters.collection_id === null ? { collection_id: "__null__" } : {}),
          ...(pageParam ? { cursor: pageParam } : {}),
        },
      });
    },
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    enabled: isAuthenticated && enabled,
    staleTime: 30_000,
    refetchInterval: false,
    refetchOnWindowFocus: false,
  });

  // loadAll : draine page apres page jusqu'a epuisement du curseur. No-op une
  // fois `hasNextPage` false. react-query dedupe les fetchNextPage concurrents.
  useEffect(() => {
    if (loadAll && hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [loadAll, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Jointure par `slug` (= Creator.name cote Neon = Convex creators.slug),
  // pas par `id` : Convex._id (string opaque) != UUID Neon, donc joindre par
  // ID donne 0 match. Le slug est la SEULE cle partagee entre les 2 plans.
  const creatorBySlug = useMemo(() => {
    const map = new Map<string, CreatorLookup>();
    for (const c of creators) map.set(c.slug, { name: c.name, color: c.color });
    return map;
  }, [creators]);

  const projected = useMemo(() => {
    const pages = data?.pages;
    if (!pages) return [];
    const out: VaultItem[] = [];
    for (const page of pages) {
      if (!Array.isArray(page.items)) continue;
      for (const it of page.items) {
        out.push(projectToUi(it, creatorBySlug.get(it.creator_slug)));
      }
    }
    return out;
  }, [data, creatorBySlug]);

  const setItems = useSetAtom(vaultItemsAtom);
  useEffect(() => {
    setItems(projected);
  }, [projected, setItems]);

  // Stable identity : evite que les consumers (IntersectionObserver de la
  // grille) recreent leur effet a chaque render.
  const fetchNext = useCallback(() => {
    void fetchNextPage();
  }, [fetchNextPage]);

  return {
    items: projected,
    isLoading,
    error: error as Error | null,
    fetchNextPage: fetchNext,
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
    // `find` (pas pages[0]) : robuste si la 1re page est momentanement en
    // refetch. Le backend ne renseigne `total` que sur la page sans cursor.
    total: data?.pages.find((p) => p.total != null)?.total ?? null,
  };
}


/* ====================================================================== */
/* useCommitVaultEnrichments                                               */
/* ====================================================================== */

interface CommitOptions {
  onSuccess?: () => void;
  onError?: (err: Error) => void;
}

/**
 * Commit batch des drafts pending vers le backend.
 *
 * Pour chaque entry de `pendingEnrichmentsAtom` : PATCH /api/vault/items/{id}
 * avec {subject, location, action}. En parallele (Promise.all). Quand tout
 * est OK : clear pendingEnrichmentsAtom + invalidate la query vault → refetch.
 *
 * Si une PATCH fail : on garde les pendings (l'user retentera) et on bubble
 * l'erreur via onError.
 */
export function useCommitVaultEnrichments(): {
  commit: (opts?: CommitOptions) => Promise<void>;
  isPending: boolean;
} {
  const setPending = useSetAtom(pendingEnrichmentsAtom);
  const pending = useAtomValue(pendingEnrichmentsAtom);
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async () => {
      const entries = Object.entries(pending);
      if (entries.length === 0) return;
      await Promise.all(
        entries.map(([itemId, draft]) =>
          cyranoApi.patch(`/api/vault/items/${itemId}`, draft),
        ),
      );
    },
    onSuccess: () => {
      setPending({});
      queryClient.invalidateQueries({ queryKey: ["vault", "list"] });
    },
  });

  async function commit(opts?: CommitOptions) {
    try {
      await mutation.mutateAsync();
      opts?.onSuccess?.();
    } catch (e) {
      opts?.onError?.(e as Error);
    }
  }

  return { commit, isPending: mutation.isPending };
}


/* ====================================================================== */
/* useRenameVaultItem                                                      */
/* ====================================================================== */

/** Mutation hook : PATCH /api/vault/items/{id} avec `custom_name` seul —
 *  renommage cockpit (libellé d'affichage), ISOLÉ de l'IA. `name` vide ("")
 *  efface le nom perso (retour au titre dérivé). Invalide la liste pour refetch.
 */
export function useRenameVaultItem(): {
  rename: (itemId: string, name: string) => Promise<void>;
  isPending: boolean;
} {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (input: { itemId: string; name: string }) => {
      await cyranoApi.patch(`/api/vault/items/${input.itemId}`, {
        custom_name: input.name,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vault", "list"] });
    },
  });

  async function rename(itemId: string, name: string) {
    await mutation.mutateAsync({ itemId, name });
  }

  return { rename, isPending: mutation.isPending };
}

/* ====================================================================== */
/* useToggleHeraldEnabled                                                  */
/* ====================================================================== */

/** Mutation hook : PATCH /api/vault/items/{id} avec `herald_enabled` seul —
 *  coche/décoche « Herald » sur CE média (pool CTA de canal, sélection PAR
 *  MÉDIA, indépendante du dossier). ISOLÉ de l'IA (pas de reindex). Invalide
 *  la liste pour refetch. */
export function useToggleHeraldEnabled(): {
  toggle: (itemId: string, enabled: boolean) => Promise<void>;
  isPending: boolean;
} {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (input: { itemId: string; enabled: boolean }) => {
      await cyranoApi.patch(`/api/vault/items/${input.itemId}`, {
        herald_enabled: input.enabled,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vault", "list"] });
    },
  });

  async function toggle(itemId: string, enabled: boolean) {
    await mutation.mutateAsync({ itemId, enabled });
  }

  return { toggle, isPending: mutation.isPending };
}

/* ====================================================================== */
/* useLangueDuVocal                                                         */
/* ====================================================================== */

/** Mutation hook : PATCH /api/vault/items/{id} avec `langue` seul — la
 *  langue d'un vocal posée À LA MAIN (source `main` côté serveur, jamais
 *  réécrite par une machine). `null` l'efface : « c'est un gémissement, il
 *  n'a pas de langue ». ISOLÉ de l'IA. Invalide la liste pour refetch. */
export function useLangueDuVocal(): {
  poser: (itemId: string, langue: string | null) => Promise<void>;
  isPending: boolean;
} {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (input: { itemId: string; langue: string | null }) => {
      await cyranoApi.patch(`/api/vault/items/${input.itemId}`, {
        langue: input.langue,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vault", "list"] });
    },
  });

  async function poser(itemId: string, langue: string | null) {
    await mutation.mutateAsync({ itemId, langue });
  }

  return { poser, isPending: mutation.isPending };
}

/* ====================================================================== */
/* useBulkSetCatalogTag                                                     */
/* ====================================================================== */

/** La VRAIE raison d'un échec de requête, lisible par Kayla.
 *
 *  🩸 12-09-2026 : l'écran avalait toute erreur (réseau, session expirée, 4xx…)
 *  et affichait « média illisible ou trop volumineux » — une cause inventée.
 *  Kayla a testé une vidéo de 9 s et lu « trop volumineux » alors qu'AUCUNE
 *  requête n'atteignait le serveur. Une absence d'info n'est pas une valeur :
 *  on nomme l'erreur telle qu'elle est. */
export function messageDErreur(e: unknown): string {
  if (e instanceof TimeoutError) return e.message;
  if (e instanceof ApiError) {
    return e.detail ? `HTTP ${e.status} — ${e.detail}` : `HTTP ${e.status}`;
  }
  if (e instanceof TypeError) {
    // `fetch` rejette en TypeError quand la requête ne PART pas (réseau coupé,
    // origine non autorisée, bloqueur) — le message brut « Failed to fetch »
    // ne dit rien à Kayla.
    return "Impossible de joindre le serveur (la requête n'est pas partie)";
  }
  if (e instanceof Error) return e.message;
  return String(e);
}

/** Résultat d'un marquage en masse : combien d'items ont bien été taggés, et
 *  les ids qui ont échoué (PATCH rejeté) — l'appelant les garde sélectionnés
 *  pour retenter, plutôt que de tout dé-cocher. `error` = la première erreur
 *  de REQUÊTE rencontrée (réseau, session, 4xx/5xx), pour la montrer telle
 *  quelle plutôt qu'un compte opaque. */
export interface BulkTagResult {
  ok: number;
  failed: string[];
  error: string | null;
}

/** Nombre de PATCH menés en parallèle. Le marquage en masse peut porter sur des
 *  centaines de médias ; les lancer tous d'un coup (`Promise.all`) noierait le
 *  backend et l'auth proxy. Un petit pool reste rapide sans pic. */
const BULK_TAG_CONCURRENCY = 6;

/** Mutation hook : marque N médias « Payant » (`["paid"]`), « Normal / gratuit »
 *  (`["free"]`) ou « Non taggé » (`[]`) en écrivant `catalog_tags` via le PATCH
 *  par-item EXISTANT (`/api/vault/items/{id}`) — donc actif sans attendre aucun
 *  déploiement backend. Le backend impose déjà la mutex free⊥paid : on envoie
 *  un tag unique, pas un ajout.
 *
 *  Écrit par petits lots concurrents (cf `BULK_TAG_CONCURRENCY`) et n'invalide
 *  la liste qu'une fois TOUT terminé (un seul refetch, pas un par item). Les ids
 *  en échec sont rendus pour que l'appelant les garde cochés. */
export function useBulkSetCatalogTag(): {
  run: (itemIds: string[], tag: "free" | "paid" | null) => Promise<BulkTagResult>;
  isPending: boolean;
  /** Progression `done`/`total` pendant le marquage (libellé « 12/40 »). */
  done: number;
  total: number;
} {
  const queryClient = useQueryClient();
  const [isPending, setIsPending] = useState(false);
  const [done, setDone] = useState(0);
  const [total, setTotal] = useState(0);

  async function run(
    itemIds: string[],
    tag: "free" | "paid" | null,
  ): Promise<BulkTagResult> {
    const ids = [...new Set(itemIds)];
    if (ids.length === 0) return { ok: 0, failed: [], error: null };
    const catalog_tags = tag === null ? [] : [tag];
    setIsPending(true);
    setTotal(ids.length);
    setDone(0);
    const failed: string[] = [];
    let error: string | null = null;
    let ok = 0;
    let cursor = 0;
    async function worker() {
      while (cursor < ids.length) {
        const id = ids[cursor++]!;
        try {
          await cyranoApi.patch(`/api/vault/items/${id}`, { catalog_tags });
          ok++;
        } catch (e) {
          failed.push(id);
          error ??= messageDErreur(e);
        } finally {
          setDone((n) => n + 1);
        }
      }
    }
    try {
      await Promise.all(
        Array.from({ length: Math.min(BULK_TAG_CONCURRENCY, ids.length) }, worker),
      );
    } finally {
      setIsPending(false);
      queryClient.invalidateQueries({ queryKey: ["vault", "list"] });
    }
    return { ok, failed, error };
  }

  return { run, isPending, done, total };
}

/* ====================================================================== */
/* usePublishToDropp                                                        */
/* ====================================================================== */

/** Un item pré-publié sur Dropfans : son id Vault + le `ctt_…` obtenu. */
export interface DroppPublishItem {
  item_id: string;
  dropp_content_id: string;
}

/** Réponse de la pré-publication en masse. */
export interface DroppPublishResult {
  /** Items téléversés (ou déjà en cache) avec leur `ctt_…`. */
  published: DroppPublishItem[];
  /** Ids qui ont échoué côté backend (média introuvable, upload Dropp KO…). */
  failed: string[];
  /** true si la route n'est pas (encore) servie → rien n'a été tenté. Le
   *  cockpit se déploie AVANT Cyrano : pendant la fenêtre, l'UI le DIT au lieu
   *  d'afficher une erreur brute (le marquage « Payant » a déjà été appliqué,
   *  et l'upload se fera de toute façon à la première vente). */
  unavailable: boolean;
  /** Première erreur de REQUÊTE (réseau, session expirée, 4xx/5xx, timeout) —
   *  distincte de `failed`, qui sont des échecs PAR MÉDIA rendus par le
   *  serveur. Quand `error` est posé, les ids du lot sont aussi dans `failed`
   *  (pour le ré-essai) mais la cause n'est PAS le média : l'UI doit montrer
   *  `error`, jamais « illisible ou trop volumineux ». */
  error: string | null;
}

interface PublishToDroppResponse {
  published?: DroppPublishItem[];
  failed?: string[];
}

/** Taille d'un lot envoyé au backend. Le backend borne `item_ids` à 50 : au-delà
 *  d'un seul POST, il rendait 422 et l'écran affichait « 600 échecs » alors que
 *  rien n'avait été tenté. On DÉCOUPE donc, et petit : chaque média est
 *  téléversé côté serveur (Telegram → Cloudflare, jusqu'à 200 Mo), un lot trop
 *  gros dépasserait le timeout de la requête. */
const DROPP_PUBLISH_CHUNK = 5;
/** Marge large : un lot de 5 gros médias (download + upload) peut être long. */
const DROPP_PUBLISH_TIMEOUT_MS = 180_000;

/** Hook : pré-publie N médias sur Dropfans — téléverse chaque média via l'API
 *  officielle Dropp et met en cache le `ctt_…` dans
 *  `content_items.metadata["dropp_content_id"]` (endpoint backend
 *  `POST /api/vault/items/publish-dropp`).
 *
 *  DÉCOUPE en lots de {@link DROPP_PUBLISH_CHUNK} (le backend refuse > 50, et un
 *  lot trop gros dépasse le timeout). Agrège `published`/`failed` sur tous les
 *  lots, expose la progression, et n'invalide la liste qu'à la fin. GARDÉ : si
 *  le PREMIER lot rend 404 (backend pas déployé), on s'arrête et on renvoie
 *  `unavailable:true` SANS lever. */
export function usePublishToDropp(): {
  publish: (itemIds: string[]) => Promise<DroppPublishResult>;
  isPending: boolean;
  /** Progression `done`/`total` (en MÉDIAS) pendant la publication. */
  done: number;
  total: number;
} {
  const queryClient = useQueryClient();
  const [isPending, setIsPending] = useState(false);
  const [done, setDone] = useState(0);
  const [total, setTotal] = useState(0);

  async function publish(itemIds: string[]): Promise<DroppPublishResult> {
    const ids = [...new Set(itemIds)];
    if (ids.length === 0)
      return { published: [], failed: [], unavailable: false, error: null };

    setIsPending(true);
    setTotal(ids.length);
    setDone(0);
    const published: DroppPublishItem[] = [];
    const failed: string[] = [];
    let error: string | null = null;
    try {
      for (let i = 0; i < ids.length; i += DROPP_PUBLISH_CHUNK) {
        const lot = ids.slice(i, i + DROPP_PUBLISH_CHUNK);
        try {
          const r = await cyranoApi.post<PublishToDroppResponse>(
            "/api/vault/items/publish-dropp",
            { item_ids: lot },
            { timeout: DROPP_PUBLISH_TIMEOUT_MS },
          );
          published.push(...(r.published ?? []));
          failed.push(...(r.failed ?? []));
        } catch (e) {
          if (e instanceof ApiError && e.status === 404 && i === 0) {
            // Route absente (backend pas déployé) : rien n'a été tenté.
            return { published: [], failed: [], unavailable: true, error: null };
          }
          // Un lot en erreur (réseau, session, 5xx, timeout…) ne tue pas les
          // suivants : ses ids vont dans `failed` (ré-essai), et on RETIENT la
          // vraie cause pour l'afficher — pas une raison inventée.
          failed.push(...lot);
          error ??= messageDErreur(e);
        } finally {
          setDone((n) => n + lot.length);
        }
      }
      return { published, failed, unavailable: false, error };
    } finally {
      setIsPending(false);
      queryClient.invalidateQueries({ queryKey: ["vault", "list"] });
    }
  }

  return { publish, isPending, done, total };
}

/* ====================================================================== */
/* useImportToVault                                                        */
/* ====================================================================== */

/** Un import en cours, du point de vue de l'écran. */
export interface VaultImportProgress {
  /** Nom du fichier en cours (affiché tel quel : c'est le repère de Kayla). */
  fichier: string;
  /** Rang du fichier dans le lot, 1-indexé. */
  index: number;
  total: number;
  /** `posting` = ça monte vers Telegram ; `indexing` = c'est posté, on attend
   *  que le Vault le voie. Les deux durent, et pas pour la même raison — les
   *  confondre en « chargement… » rend l'attente inexplicable. */
  etape: "posting" | "indexing";
}

/** Combien de temps on attend qu'un média importé apparaisse dans le Vault.
 *
 *  L'indexation est asynchrone (worker réveillé par la queue). Ce n'est PAS un
 *  timeout d'erreur : passé ce délai, le média finira d'être indexé quand même
 *  — on arrête juste de le surveiller, et l'écran le dit. Mettre une valeur
 *  courte ferait conclure « ça n'a pas marché » sur un média parfaitement en
 *  route ; le worker dort à zéro réplique et doit être réveillé par KEDA. */
const IMPORT_INDEX_TIMEOUT_MS = 90_000;
const IMPORT_POLL_INTERVAL_MS = 1_500;

interface ImportPostResponse {
  chat_id: number;
  message_id: number;
  media_type: string;
  file_size: number;
}

/** Mutation hook : importe des fichiers depuis l'ordinateur vers le Vault.
 *
 *  Le fichier est posté dans le groupe Vault de la créatrice puis indexé par le
 *  chemin ORDINAIRE — il devient un `content_item` comme les autres. On n'a
 *  donc rien de particulier à faire ensuite : il est utilisable partout.
 *
 *  Rend les ids des médias effectivement indexés, pour que l'appelant puisse
 *  les pré-sélectionner. Un fichier encore en cours d'indexation au bout de
 *  90 s n'est PAS rendu (on ne connaît pas son id) — il apparaîtra tout seul
 *  dans la grille au rafraîchissement suivant. */
export function useImportToVault(): {
  importFiles: (
    creatorId: string,
    files: File[],
    collectionId?: string | null,
  ) => Promise<string[]>;
  isPending: boolean;
  progress: VaultImportProgress | null;
  error: string | null;
  clearError: () => void;
} {
  const queryClient = useQueryClient();
  const [progress, setProgress] = useState<VaultImportProgress | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function attendreIndexation(
    chatId: number,
    messageId: number,
  ): Promise<string | null> {
    const limite = Date.now() + IMPORT_INDEX_TIMEOUT_MS;
    while (Date.now() < limite) {
      const r = await cyranoApi.get<{ item_id: string | null }>(
        "/api/vault/import/status",
        { params: { chat_id: chatId, message_id: messageId } },
      );
      if (r.item_id) return r.item_id;
      await new Promise((resolve) =>
        setTimeout(resolve, IMPORT_POLL_INTERVAL_MS),
      );
    }
    return null;
  }

  async function importFiles(
    creatorId: string,
    files: File[],
    // Le dossier où Kayla REGARDE au moment d'importer. Absent = « 📥 Importés »,
    // créé à la volée côté worker. Sans ce paramètre, un import fait depuis un
    // dossier atterrissait ailleurs que là où elle venait de le déposer.
    collectionId?: string | null,
  ): Promise<string[]> {
    setError(null);
    const ids: string[] = [];
    try {
      // En SÉRIE, pas en parallèle : chaque fichier peut peser 50 Mo, et le
      // navigateur comme l'API encaissent mal N envois simultanés. Kayla voit
      // avancer le lot fichier par fichier, ce qui vaut mieux qu'une barre
      // unique qui ne bouge pas pendant trois minutes.
      for (let i = 0; i < files.length; i++) {
        const file = files[i]!;
        setProgress({
          fichier: file.name,
          index: i + 1,
          total: files.length,
          etape: "posting",
        });
        const form = new FormData();
        form.append("file", file);
        form.append("creator_id", creatorId);
        if (collectionId) form.append("collection_id", collectionId);
        const posted = await cyranoApi.postForm<ImportPostResponse>(
          "/api/vault/import",
          form,
        );

        setProgress({
          fichier: file.name,
          index: i + 1,
          total: files.length,
          etape: "indexing",
        });
        const id = await attendreIndexation(posted.chat_id, posted.message_id);
        if (id) ids.push(id);
      }
    } catch (e) {
      // Le message du backend est écrit POUR Kayla (« le bot n'est plus dans le
      // groupe », « 62 Mo — le maximum est de 50 Mo ») : on le montre tel quel
      // plutôt que de le remplacer par « échec de l'import », qui ne dit pas
      // quoi faire.
      setError(e instanceof Error ? e.message : "L'import a échoué.");
    } finally {
      setProgress(null);
      queryClient.invalidateQueries({ queryKey: ["vault", "list"] });
    }
    return ids;
  }

  return {
    importFiles,
    isPending: progress !== null,
    progress,
    error,
    clearError: () => setError(null),
  };
}

/* ====================================================================== */
/* useSetHeraldPool · useSetCyranoPool — poser un droit PAR MÉDIA, par lots */
/* ====================================================================== */

export interface UseSetPoolResult {
  setMany: (itemIds: string[], enabled: boolean) => Promise<number>;
  isPending: boolean;
}

/** (Dé)coche un droit par média sur un LOT — le moteur des deux hooks publics.
 *
 *  Pourquoi une route en masse en plus du toggle unitaire : un pool se compose
 *  par dizaines. Tant que la case Herald n'était posable qu'un média à la fois,
 *  personne ne l'a posée — 0 média coché sur 3 656, mesuré le 22-08-2026 — et
 *  la sélection s'est rabattue sur une propriété qui se remplit toute seule
 *  (`content_category = 'sfw'`). Une vidéo d'un dossier de script est partie
 *  dans un canal public ce jour-là. Un droit qu'on ne peut pas poser par lots
 *  n'est pas un droit, c'est un formulaire.
 *
 *  Rend le nombre de lignes RÉELLEMENT modifiées (le backend ignore ce qui est
 *  hors scope et ce qui était déjà dans l'état demandé) : c'est ce compte qui
 *  s'affiche, jamais la taille du lot envoyé.
 *
 *  🪶 Factorisé le 22-08, quand Cyrano a eu besoin du même geste trois heures
 *  après Herald. Deux copies de ce hook auraient divergé sur l'invalidation —
 *  celle qu'on oublie est celle qui laisse une grille périmée à l'écran. */
function useSetPool(route: string, cacheKey: string): UseSetPoolResult {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (input: { itemIds: string[]; enabled: boolean }) => {
      const res = await cyranoApi.post<{ updated: number }>(route, {
        item_ids: input.itemIds,
        enabled: input.enabled,
      });
      return res.updated;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vault", "list"] });
      queryClient.invalidateQueries({ queryKey: [cacheKey, "pool"] });
    },
  });

  async function setMany(itemIds: string[], enabled: boolean): Promise<number> {
    if (itemIds.length === 0) return 0;
    return mutation.mutateAsync({ itemIds, enabled });
  }

  return { setMany, isPending: mutation.isPending };
}

/** POST /api/vault/items/herald — ce qui a le droit de partir en canal PUBLIC. */
export function useSetHeraldPool(): UseSetPoolResult {
  return useSetPool("/api/vault/items/herald", "herald");
}

/** POST /api/vault/items/cyrano — ce que CYRANO a le droit d'envoyer.
 *
 *  Kayla, 22-08-2026 : « j'aimerais choisir les médias du Vault qu'il a le
 *  droit d'envoyer ». Sa liste REMPLACE les filtres automatiques — décocher le
 *  dernier média coupe les cadeaux de cette créatrice, sans repli. */
export function useSetCyranoPool(): UseSetPoolResult {
  return useSetPool("/api/vault/items/cyrano", "cyrano");
}

/* ====================================================================== */
/* useDeleteVaultItem                                                      */
/* ====================================================================== */

/** Mutation hook : DELETE /api/vault/items/{id} + drop le draft pending +
 * invalidate la query list pour refetch.
 */
export function useDeleteVaultItem(): {
  remove: (itemId: string) => Promise<void>;
  isPending: boolean;
} {
  const setPending = useSetAtom(pendingEnrichmentsAtom);
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (itemId: string) => {
      await cyranoApi.delete(`/api/vault/items/${itemId}`);
      return itemId;
    },
    onSuccess: (itemId) => {
      // Drop la draft pending eventuelle pour cet item.
      setPending((cur) => {
        if (!(itemId in cur)) return cur;
        const next = { ...cur };
        delete next[itemId];
        return next;
      });
      queryClient.invalidateQueries({ queryKey: ["vault", "list"] });
    },
  });

  async function remove(itemId: string) {
    await mutation.mutateAsync(itemId);
  }

  return { remove, isPending: mutation.isPending };
}
